// COP Copilot runtime — config (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED from takbot services/agent-runtime/src/config.ts. The point is UNCHANGED: iterate the
// pre-prompt, the context-window budgets, and the reasoning mode WITHOUT editing code — all
// env-driven on top of DEFAULT_CONTEXT_CONFIG.
//
// THE ONE ADAPTATION (browser): takbot read `process.env` + `node:fs.readFileSync` directly.
// The COP runtime runs in the browser, so env goes through the defensive `readEnv` seam (env.ts)
// and the TAKBOT_SYSTEM_PROMPT_FILE *file* path is replaced by an env-STRING override
// (TAKBOT_SYSTEM_PROMPT) — a browser cannot read an arbitrary file, but the fork-the-prompt knob
// is preserved as an inline string. Everything else is verbatim.
//
//   ┌──────────────────────────────────────────────────────────────────────────────────────┐
//   │  THE REASONING TOGGLE (first-class, always present):                                   │
//   │    TAKBOT_REASONING_MODE=instant  (default) one grammar-constrained call (eval-EARNED)  │
//   │    TAKBOT_REASONING_MODE=think              two-phase: free reason → constrained emit    │
//   └──────────────────────────────────────────────────────────────────────────────────────┘
//
//  Other env knobs (all optional; fall back to DEFAULT_CONTEXT_CONFIG):
//    TAKBOT_MAX_STEPS, TAKBOT_CTX_BUDGET, TAKBOT_DOC_TRUNC, TAKBOT_TOP_K,
//    TAKBOT_TEMPERATURE, TAKBOT_SEED, TAKBOT_MAX_TOKENS
//    TAKBOT_SYSTEM_PROMPT=<inline string>  — fork the analyst prompt without touching the binary
//    TAKBOT_CONSTRAIN_JSON=0               — disable grammar-constrained decoding (debug only)

import { DEFAULT_CONTEXT_CONFIG } from "./types.ts";
import type { ContextWindowConfig } from "./types.ts";
import { SYSTEM_PROMPT } from "./prompt.ts";
import { readEnv } from "./env.ts";

function num(envVar: string, fallback: number): number {
  const raw = readEnv(envVar);
  if (raw === undefined || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

/** Build the active ContextWindowConfig from env overrides on DEFAULT_CONTEXT_CONFIG. */
export function loadContextConfig(): ContextWindowConfig {
  const d = DEFAULT_CONTEXT_CONFIG;
  const mode = readEnv("TAKBOT_REASONING_MODE");
  return {
    ...d,
    reasoning_mode: mode === "instant" || mode === "think" ? mode : d.reasoning_mode,
    max_steps: Math.max(1, num("TAKBOT_MAX_STEPS", d.max_steps)),
    ctx_budget_chars: num("TAKBOT_CTX_BUDGET", d.ctx_budget_chars),
    doc_trunc_chars: num("TAKBOT_DOC_TRUNC", d.doc_trunc_chars),
    top_k: num("TAKBOT_TOP_K", d.top_k),
    temperature: num("TAKBOT_TEMPERATURE", d.temperature),
    seed: num("TAKBOT_SEED", d.seed),
    max_tokens: num("TAKBOT_MAX_TOKENS", d.max_tokens),
  };
}

let cachedPrompt: string | null = null;
/** The active system prompt — TAKBOT_SYSTEM_PROMPT (inline string) if set, else the default
 *  all-source-analyst prompt. Cached after first load. (takbot read a FILE here; the browser
 *  runtime takes an inline-string override instead — same fork-the-prompt knob, no fs.) */
export function getSystemPrompt(): string {
  if (cachedPrompt !== null) return cachedPrompt;
  const inline = readEnv("TAKBOT_SYSTEM_PROMPT");
  cachedPrompt = inline && inline.trim().length > 0 ? inline : SYSTEM_PROMPT;
  return cachedPrompt;
}

/** Test/host hook: clear the cached prompt so a fresh getSystemPrompt() re-reads the env. */
export function resetSystemPromptCache(): void {
  cachedPrompt = null;
}

/** Whether grammar-constrained decoding is on (default ON; TAKBOT_CONSTRAIN_JSON=0 disables). */
export function constrainEnabled(): boolean {
  return readEnv("TAKBOT_CONSTRAIN_JSON") !== "0";
}
